import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { MapContainer, TileLayer } from "react-leaflet";
import L from "leaflet";
import { GestureHandling } from "leaflet-gesture-handling";
import MarkerClusterGroup from "react-leaflet-cluster";
import { FormattedMessage, useIntl } from "react-intl";

import Button from "components/atoms/Button/Button";
import { ReactComponent as MarkerIcon } from "assets/images/icons/Marker.svg";
import { useGetAllShipments } from "api/queries/shipments/useGetAllShipments";
import Markers from "./Markers";
import MapLoadingSkeleton from "./MapLoadingSkeleton";

import "./MapWidget.scss";
import "leaflet-gesture-handling/dist/leaflet-gesture-handling.css";
import "leaflet/dist/leaflet.css";

L.Map.addInitHook("addHandler", "gestureHandling", GestureHandling);

const CARTO_BASEMAP_KEY = import.meta.env.REACT_APP_CARTO_BASEMAP_KEY;

const MapWidget = function ({ isPreview, widgetId, handleWidgetDialogOpen, isLayoutLocked }) {
  window.dispatchEvent(new Event("resize"));

  const intl = useIntl();
  const [markerPosition, setMarkerPosition] = useState("origin");

  const { data, isLoading } = useGetAllShipments();

  // Gets rid of leaflet tile pane in the right lower corner
  const controlAttribute = document.getElementsByClassName("leaflet-control-attribution");
  useEffect(() => {
    if (controlAttribute.length > 0) controlAttribute[0].style.display = "none";
  }, []);

  // Returns the number of markers that contain the given status
  const getStatusCount = (markers, status) => {
    let fileNameSubstring = "";
    switch (status) {
      case "InTransit":
        fileNameSubstring = "Blue";
        break;
      case "Delayed":
        fileNameSubstring = "Orange";
        break;
      case "Incomplete":
        fileNameSubstring = "Yellow";
        break;
      case "Booked":
        fileNameSubstring = "Black";
        break;
      default:
        fileNameSubstring = "Black";
        break;
    }

    const statusCount = markers.filter((marker) => marker.options.icon.options.iconUrl.includes(fileNameSubstring));
    return statusCount.length;
  };

  const generateClusterIcon = (cluster) => {
    const markers = cluster.getAllChildMarkers();
    const markerCount = cluster.getChildCount();

    const inTransitCount = getStatusCount(markers, "InTransit");
    const delayedCount = getStatusCount(markers, "Delayed");
    const incompleteCount = getStatusCount(markers, "Incomplete");
    const bookedCount = getStatusCount(markers, "Booked");

    // calculate the percentage of each status for the conic-gradient
    const inTransitDegreesEnd = (inTransitCount / markerCount) * 100;
    const delayedDegreesEnd = inTransitDegreesEnd + (delayedCount / markerCount) * 100;
    const incompleteDegreesEnd = delayedDegreesEnd + (incompleteCount / markerCount) * 100;
    const bookedDegreesEnd = incompleteDegreesEnd + (bookedCount / markerCount) * 100;

    return L.divIcon({
      html: `<div class="donut" style="background: conic-gradient(
      #298CDE 0% ${inTransitDegreesEnd}%,
      #F1B92A ${inTransitDegreesEnd}% ${delayedDegreesEnd}%,
      #EA7939 ${delayedDegreesEnd}% ${incompleteDegreesEnd}%,
      #252525 ${incompleteDegreesEnd}% ${bookedDegreesEnd}%
      );"><div class="hole">${markerCount}</div></div>`,
      className: "marker-cluster-custom",
      iconSize: L.point(40, 40, true),
    });
  };

  return (
    <MapContainer
      className="map-widget"
      center={[35.377956, 4.89707]}
      zoom={2}
      style={{ height: "100%", borderRadius: "1.7rem" }}
      minZoom={2}
      maxZoom={20}
      scrollWheelZoom
      maxBounds={[
        [-90, -180],
        [90, 180],
      ]}
      gestureHandling
    >
      {isLoading && !data?.length > 0 && <MapLoadingSkeleton />}
      <div style={{ position: "relative", height: 250 }}>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url={`https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png?key=${CARTO_BASEMAP_KEY}`}
        />
        <div
          style={{
            position: "absolute",
            top: "10px",
            right: "10px",
            zIndex: "1000",
            display: "flex",
            alignItems: "center",
          }}
        >
          <Button
            variant="primary"
            onClick={() => {
              setMarkerPosition(markerPosition === "origin" ? "destination" : "origin");
            }}
            className="leaflet-touch leaflet-bar bg-white"
            style={{
              borderRadius: 10,
              color: "black",
              fontWeight: 500,
            }}
          >
            <MarkerIcon />
            <span className="ml-1">
              {markerPosition === "origin"
                ? intl.formatMessage({ id: "general.origin", defaultMessage: "Origin" })
                : intl.formatMessage({ id: "general.destination", defaultMessage: "Destination" })}
            </span>
          </Button>
          {!isLayoutLocked && !isPreview && (
            <Button
              className="map-widget-edit ml-2"
              onClick={() => {
                handleWidgetDialogOpen(widgetId);
              }}
            >
              <FormattedMessage id="general.edit" defaultMessage="Edit" />
            </Button>
          )}
        </div>
        <MarkerClusterGroup
          chunkedLoading
          showCoverageOnHover={false}
          maxClusterRadius={150}
          iconCreateFunction={(cluster) => generateClusterIcon(cluster)}
        >
          <Markers mapData={data} markerPosition={markerPosition} />
        </MarkerClusterGroup>
      </div>
    </MapContainer>
  );
};

MapWidget.propTypes = {
  isLayoutLocked: PropTypes.bool,
  isPreview: PropTypes.bool,
  widgetId: PropTypes.number,
  handleWidgetDialogOpen: PropTypes.func,
};

MapWidget.defaultProps = {
  isLayoutLocked: false,
  isPreview: false,
  widgetId: null,
  handleWidgetDialogOpen: () => {},
};

export default MapWidget;
